import { Identifiable, Reference } from '@approvalmax/types';
import {
    Checkbox,
    CheckboxEditor,
    DropdownEditor,
    FocusImplementer,
    NumberEditor,
    prioritizeAndMapDropdownItems,
    TextAreaEditor,
} from '@approvalmax/ui';
import { Button as NewButton } from '@approvalmax/ui/src/components';
import { intl } from '@approvalmax/utils';
import { useAtomValue, useSetAtom } from 'jotai';
import { constants, selectors } from 'modules/common';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { TwoDifferentValuesWarning, WarningIndicator, WarningLevel } from 'modules/request';
import { AddMatchingIcon, MatchingIcon } from 'modules/sprites';
import { useCallback, useMemo } from 'react';
import { ElementFactory } from 'react-bem-factory';
import { useDispatch } from 'react-redux';
import {
    ComponentErrorBoundary,
    QBooksMatchingInfo,
    type QBooksMatchingInfoProps,
    qBooksMatchingPopupAccountLineItemState,
} from 'shared/components';
import { getContactDisplayNameByIntegrationCode } from 'shared/helpers';
import { useCompanyBasedOnUrl, useCompanyBetaFeatures } from 'shared/hooks';
import { ocrIsLoadingState } from 'shared/states';

import {
    changeQbooksAccountLineItemAmount,
    changeQbooksAccountLineItemChecked,
    changeQbooksAccountLineItemDescription,
    changeQbooksAccountLineItemsAccount,
    changeQbooksAccountLineItemsAllChecked,
    changeQbooksAccountLineItemsClass,
    changeQbooksAccountLineItemsCustomer,
    changeQbooksAccountLineItemsTaxCode,
    toggleQbooksAccountLineItemIsBillable,
    toggleQbooksAccountLineItemIsTaxable,
} from '../../../../../actions';
import { CellIndicator, ReadOnlyCell } from '../../../../../components';
import { getClassesWithNesting, QBooksClass } from '../../../../../data/qbooks/QBooksClass';
import { getShowValidationErrors } from '../../../../../selectors/pageSelectors';
import { getQBooksContext } from '../../../../../selectors/qbooks';
import { ColumnDefinition } from '../../../../../selectors/types/ColumnDefinition';
import { ExpandedQBooksAccountLineItem } from '../../../../../selectors/types/ExpandedQBooksAccountLineItem';
import { defaultOnChangeFieldRequiredForMatching } from '../../../QBooksBillForm/QBooksBillForm.helpers';
import type { OnChangeFieldRequiredForMatching } from '../../../QBooksBillForm/QBooksBillForm.types';
import { QBooksLineItemCustomerCell } from '../../../QBooksLineItemCustomerCell/QBooksLineItemCustomerCell';
import { QbooksLineItemDropdownCell } from '../../../QbooksLineItemDropdownCell/QbooksLineItemDropdownCell';
import { messages } from '../QBooksAccountLineItemsSection.messages';
import { useAccountLineItems } from './useAccountLineItems';
import { useIsApproverMode } from './useIsApproverMode';
import { useIsManualMatchingShown } from './useIsManualMatchingShown';
import { useNormalize } from './useNormalize';
import { useOcrLinesItems } from './useOcrLinesItems';
import { useRequestExpanded } from './useRequest';
import { useRequiredFields } from './useRequiredFields';
import { useTaxCodes } from './useTaxCodes';

export const useColumnDefinitions = (options: {
    qa: ElementFactory;
    bem: ElementFactory;
    onChange?: OnChangeFieldRequiredForMatching | typeof defaultOnChangeFieldRequiredForMatching;
    onRemoveMatching?: QBooksMatchingInfoProps['onRemoveMatching'];
}) => {
    const { qa, bem, onRemoveMatching, onChange = defaultOnChangeFieldRequiredForMatching } = options;

    const dispatch = useDispatch();
    const request = useRequestExpanded();

    const company = useCompanyBasedOnUrl();
    const { isQBOMatchingOnEditing } = useCompanyBetaFeatures(company.id);

    const { lineAmountType } = request.details;

    const profile = useSelector(selectors.profile.getProfileUser);

    const isApproverMode = useIsApproverMode();
    const isManualMatchingShown = useIsManualMatchingShown();
    const isOcrLoading = useAtomValue(ocrIsLoadingState);
    const isValidationErrorsShown = useSelector(getShowValidationErrors);
    const isBill = request.integrationCode === domain.IntegrationCode.QBooksBill;
    const isExpense = request.integrationCode === domain.IntegrationCode.QBooksExpense;

    const isMatchingAvailable = request.flags.isActiveReviewer || request.flags.isAuthor;

    const isBillsRequiredDataIncomplete = !request.details.vendor?.id || !request.details.date;
    const noMatchingPossible = !isManualMatchingShown || isBillsRequiredDataIncomplete;

    const qbooksContext = useSelector(getQBooksContext);

    const taxCodesList = useTaxCodes();
    const requiredFields = useRequiredFields();
    const sortedOcrLineItems = useOcrLinesItems();
    const accountLineItems = useAccountLineItems();
    const setAccountLineItem = useSetAtom(qBooksMatchingPopupAccountLineItemState);

    const matchingInfoNormalizer = useNormalize(accountLineItems);

    const handleRemoveMatching = useCallback<QBooksMatchingInfoProps['onRemoveMatching']>(
        (lineItemId) => {
            if (onRemoveMatching) {
                return onRemoveMatching?.(lineItemId, true);
            }

            return Promise.resolve();
        },
        [onRemoveMatching]
    );

    const classes = useMemo(() => getClassesWithNesting(qbooksContext.classes), [qbooksContext.classes]);

    return useMemo<Array<ColumnDefinition<ExpandedQBooksAccountLineItem>>>(() => {
        const classColMandatory =
            qbooksContext.hasClassFeature &&
            qbooksContext.classField &&
            requiredFields.fieldIds.includes(qbooksContext.classField.id);
        const customerColMandatory =
            qbooksContext.hasCustomerFeature &&
            qbooksContext.customerField &&
            requiredFields.fieldIds.includes(qbooksContext.customerField.id);

        return [
            ...(isApproverMode
                ? []
                : [
                      {
                          id: 'checkbox',
                          name: '',
                          fixedWidth: 40,
                          headerCell: (lineItems) => {
                              const checked = lineItems.every((li) => li.checked);
                              const triState = lineItems.some((li) => li.checked);

                              const handleClick = () => {
                                  const nextState = !checked;

                                  dispatch(changeQbooksAccountLineItemsAllChecked(nextState));
                              };

                              return (
                                  <div onClick={handleClick}>
                                      <Checkbox size='size-18' triState={triState} checked={checked} />
                                  </div>
                              );
                          },
                          cell: (li) => {
                              return (
                                  <div
                                      onClick={() => {
                                          dispatch(changeQbooksAccountLineItemChecked(li.id, !li.checked));
                                      }}
                                  >
                                      <Checkbox checked={li.checked} size='size-18' />
                                  </div>
                              );
                          },
                      } as ColumnDefinition<ExpandedQBooksAccountLineItem>,
                  ]),
            {
                id: 'account',
                name: messages.colNameAccount,
                flexGrow: 1,
                readonly: () => isOcrLoading,
                invalid: (li) => {
                    if (isValidationErrorsShown) {
                        if (!li.empty && !li.account) {
                            return true;
                        }

                        if (li.account) {
                            return !!li.isBillable && !li.canBeBillable;
                        }
                    }

                    return false;
                },
                cell: (li, { invalid, readonly }, lineIndex) => {
                    let showMultipleAccountIdsWarning = false;

                    let items: Identifiable[] | Reference[] = qbooksContext.accounts;

                    if (sortedOcrLineItems) {
                        items = prioritizeAndMapDropdownItems(items, sortedOcrLineItems?.[lineIndex]?.accountIds);

                        showMultipleAccountIdsWarning = (sortedOcrLineItems?.[lineIndex]?.accountIds || []).length > 1;
                    }

                    const warningLevel = showMultipleAccountIdsWarning ? WarningLevel.Yellow : WarningLevel.None;

                    return (
                        <WarningIndicator
                            show={showMultipleAccountIdsWarning}
                            content={messages.multipleAccountsWarning({
                                contactDisplayName: getContactDisplayNameByIntegrationCode(request.integrationCode),
                                requestName: selectors.template
                                    .getTemplateDisplayNameByCode(request.integrationCode, true)
                                    ?.toLowerCase(),
                            })}
                        >
                            {readonly ? (
                                <>
                                    <ReadOnlyCell text={li.account && li.account?.text} />

                                    <CellIndicator $warningLevel={warningLevel} />
                                </>
                            ) : (
                                <>
                                    <QbooksLineItemDropdownCell
                                        qa={qa('cell-editor')}
                                        className={bem('cell', 'dropdown')}
                                        items={items}
                                        invalid={invalid}
                                        value={li.account || null}
                                        singleLinePerItem={false}
                                        onChange={(newAccount: domain.QBooksAccount | null) =>
                                            onChange(
                                                () =>
                                                    dispatch(changeQbooksAccountLineItemsAccount([li.id], newAccount)),
                                                {
                                                    isAccountLine: true,
                                                    lineId: li.id,
                                                }
                                            ).catch(() => {})
                                        }
                                        placeholder={messages.cellPlaceholderAccount}
                                        listItem={(props) => (
                                            <DropdownEditor.ListItem
                                                {...props}
                                                nestingLevel={(props.item as domain.QBooksAccount).nestingLevel}
                                            />
                                        )}
                                    />

                                    <CellIndicator $warningLevel={warningLevel} />
                                </>
                            )}
                        </WarningIndicator>
                    );
                },
            },
            {
                id: 'description',
                name: messages.colNameDescription,
                flexGrow: 2,
                readonly: () => isOcrLoading,
                invalid: () => false,
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.description} multiline />
                    ) : (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <TextAreaEditor
                                    qa={qa('cell-editor')}
                                    ref={registerFocusable}
                                    invalid={invalid}
                                    value={li.description}
                                    maxLength={constants.qBooksConstants.LINE_ITEM_DESCRIPTION_MAX_LENGTH}
                                    onChange={(newDescription) =>
                                        dispatch(changeQbooksAccountLineItemDescription(li.id, newDescription))
                                    }
                                    placeholder={messages.cellPlaceholderDescription}
                                    theme='transparent'
                                    changeOnBlur
                                />
                            )}
                        </FocusImplementer>
                    ),
            },
            ...(lineAmountType !== domain.LineAmountType.NoTax
                ? [
                      {
                          id: 'taxCode',
                          name: messages.colNameTaxCode,
                          flexGrow: 0.7,
                          readonly: () => isOcrLoading,
                          invalid: (li) =>
                              qbooksContext.isTrackingTaxesEnabled &&
                              isValidationErrorsShown &&
                              !li.empty &&
                              !li.taxCode,
                          cell: (li, { invalid, readonly }, lineIndex) => {
                              let showMultipleTaxCodesWarning = false;

                              let items: Identifiable[] | Reference[] = taxCodesList;

                              if (sortedOcrLineItems) {
                                  items = prioritizeAndMapDropdownItems(
                                      items,
                                      sortedOcrLineItems?.[lineIndex]?.accountIds
                                  );

                                  showMultipleTaxCodesWarning =
                                      // TODO: Remove when backend is ready.
                                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                      // @ts-expect-error
                                      (sortedOcrLineItems?.[lineIndex]?.taxTypes || []).length > 1;
                              }

                              const warningLevel = showMultipleTaxCodesWarning
                                  ? WarningLevel.Yellow
                                  : WarningLevel.None;

                              return (
                                  <WarningIndicator
                                      show={showMultipleTaxCodesWarning}
                                      content={messages.multipleTaxCodesWarning({
                                          contactDisplayName: getContactDisplayNameByIntegrationCode(
                                              request.integrationCode
                                          ),
                                          requestName: selectors.template
                                              .getTemplateDisplayNameByCode(request.integrationCode, true)
                                              ?.toLowerCase(),
                                      })}
                                  >
                                      {readonly ? (
                                          <>
                                              <ReadOnlyCell text={li.taxCode && li.taxCode.text} />

                                              <CellIndicator $warningLevel={warningLevel} />
                                          </>
                                      ) : (
                                          <>
                                              <QbooksLineItemDropdownCell
                                                  qa={qa('cell-editor')}
                                                  className={bem('cell', 'dropdown')}
                                                  items={items}
                                                  value={li.taxCode || null}
                                                  invalid={invalid}
                                                  onChange={(newTax: domain.QBooksTaxCode | null) =>
                                                      dispatch(changeQbooksAccountLineItemsTaxCode([li.id], newTax))
                                                  }
                                                  placeholder={messages.cellPlaceholderTaxCode}
                                              />

                                              <CellIndicator $warningLevel={warningLevel} />
                                          </>
                                      )}
                                  </WarningIndicator>
                              );
                          },
                      } as ColumnDefinition<ExpandedQBooksAccountLineItem>,
                  ]
                : []),
            ...((isBill || isExpense) && qbooksContext.hasBillableFeature
                ? [
                      {
                          id: 'billable',
                          name: messages.colNameBillable,
                          fixedWidth: 75,
                          readonly: () => isOcrLoading,
                          invalid: (li) => {
                              if (li.account) {
                                  return isValidationErrorsShown && li.isBillable && !li.canBeBillable;
                              }

                              return false;
                          },
                          cell: (li) => (
                              <div className={bem('billable-checkbox')}>
                                  <CheckboxEditor
                                      qa={qa('billable-checkbox')}
                                      value={!!li.isBillable}
                                      onChange={(newValue: boolean) =>
                                          dispatch(
                                              toggleQbooksAccountLineItemIsBillable(
                                                  li.id,
                                                  qbooksContext.hasTaxableFeature,
                                                  newValue
                                              )
                                          )
                                      }
                                  />
                              </div>
                          ),
                      } as ColumnDefinition<ExpandedQBooksAccountLineItem>,
                  ]
                : []),
            ...((isBill || isExpense) && qbooksContext.hasTaxableFeature
                ? [
                      {
                          id: 'tax',
                          name: messages.colNameTax,
                          fixedWidth: 45,
                          readonly: () => isOcrLoading,
                          cell: (li) => (
                              <div className={bem('taxable-checkbox')}>
                                  <CheckboxEditor
                                      qa={qa('taxable-checkbox')}
                                      value={!!li.isTaxable}
                                      onChange={(newValue: boolean) =>
                                          dispatch(toggleQbooksAccountLineItemIsTaxable(li.id, newValue))
                                      }
                                  />
                              </div>
                          ),
                      } as ColumnDefinition<ExpandedQBooksAccountLineItem>,
                  ]
                : []),
            ...(qbooksContext.hasCustomerFeature
                ? [
                      {
                          id: 'customer',
                          name: messages.colNameCustomer,
                          flexGrow: 1,
                          readonly: () => isOcrLoading,
                          invalid: (li) => isValidationErrorsShown && !li.empty && customerColMandatory && !li.customer,
                          cell: (li, { invalid, readonly }, lineIndex) => {
                              let showMultipleCustomersWarning = false;

                              const customerIds = sortedOcrLineItems?.[lineIndex]?.customerIds || [];
                              const customerIdsLength = customerIds.length;

                              if (sortedOcrLineItems && customerIdsLength) {
                                  showMultipleCustomersWarning = customerIdsLength > 1;
                              }

                              const warningLevel = showMultipleCustomersWarning
                                  ? WarningLevel.Yellow
                                  : WarningLevel.None;

                              return (
                                  <WarningIndicator
                                      show={showMultipleCustomersWarning}
                                      content={messages.multipleCustomersWarning({
                                          contactDisplayName: getContactDisplayNameByIntegrationCode(
                                              request.integrationCode
                                          ),
                                          requestName: selectors.template
                                              .getTemplateDisplayNameByCode(request.integrationCode, true)
                                              ?.toLowerCase(),
                                      })}
                                  >
                                      {readonly ? (
                                          <>
                                              <ReadOnlyCell text={li.customer && li.customer.text} />

                                              <CellIndicator $warningLevel={warningLevel} />
                                          </>
                                      ) : (
                                          <>
                                              <QBooksLineItemCustomerCell
                                                  qa={qa('cell-editor')}
                                                  className={bem('cell', 'dropdown')}
                                                  value={li.customer || null}
                                                  invalid={invalid}
                                                  onChange={(newCustomer: Reference | null) =>
                                                      dispatch(
                                                          changeQbooksAccountLineItemsCustomer([li.id], newCustomer)
                                                      )
                                                  }
                                                  placeholder={messages.cellPlaceholderCustomer}
                                                  lineIndex={lineIndex}
                                              />

                                              <CellIndicator $warningLevel={warningLevel} />
                                          </>
                                      )}
                                  </WarningIndicator>
                              );
                          },
                      } as ColumnDefinition<ExpandedQBooksAccountLineItem>,
                  ]
                : []),
            ...(qbooksContext.hasClassFeature
                ? [
                      {
                          id: 'class',
                          name: messages.colNameClass,
                          flexGrow: 1,
                          readonly: () => isOcrLoading,
                          invalid: (li) => isValidationErrorsShown && !li.empty && classColMandatory && !li.class,
                          cell: (li, { invalid, readonly }, lineIndex) => {
                              let showMultipleClassesWarning = false;

                              let items: Identifiable[] | Reference[] = qbooksContext.classes;

                              const classIds = sortedOcrLineItems?.[lineIndex]?.classIds || [];

                              const classIdsIdsLength = classIds.length;

                              const hasOcrClassIds = sortedOcrLineItems && classIdsIdsLength;

                              if (hasOcrClassIds) {
                                  showMultipleClassesWarning = classIdsIdsLength > 1;
                                  items = prioritizeAndMapDropdownItems(items, classIds);
                              }

                              const warningLevel = showMultipleClassesWarning ? WarningLevel.Yellow : WarningLevel.None;

                              return (
                                  <WarningIndicator
                                      show={showMultipleClassesWarning}
                                      content={messages.multipleClassesWarning({
                                          contactDisplayName: getContactDisplayNameByIntegrationCode(
                                              request.integrationCode
                                          ),
                                          requestName: selectors.template
                                              .getTemplateDisplayNameByCode(request.integrationCode, true)
                                              ?.toLowerCase(),
                                      })}
                                  >
                                      {readonly ? (
                                          <>
                                              <ReadOnlyCell text={li.class && li.class.text} />

                                              <CellIndicator $warningLevel={warningLevel} />
                                          </>
                                      ) : (
                                          <>
                                              <QbooksLineItemDropdownCell
                                                  qa={qa('cell-editor')}
                                                  className={bem('cell', 'dropdown')}
                                                  items={hasOcrClassIds ? items : classes}
                                                  value={li.class || null}
                                                  invalid={invalid}
                                                  onChange={(newClass: Reference | null) =>
                                                      dispatch(changeQbooksAccountLineItemsClass([li.id], newClass))
                                                  }
                                                  placeholder={messages.cellPlaceholderClass}
                                                  listItem={(props) => (
                                                      <DropdownEditor.ListItem
                                                          {...props}
                                                          nestingLevel={(props.item as QBooksClass).nestingLevel}
                                                      />
                                                  )}
                                              />

                                              <CellIndicator $warningLevel={warningLevel} />
                                          </>
                                      )}
                                  </WarningIndicator>
                              );
                          },
                      } as ColumnDefinition<ExpandedQBooksAccountLineItem>,
                  ]
                : []),
            {
                id: 'amount',
                name: messages.colNameAmount,
                alignRight: true,
                flexGrow: 0.7,
                readonly: () => isOcrLoading,
                invalid: (li) => isValidationErrorsShown && !li.empty && li.amount == null,
                cell: (li, { invalid, readonly }, lineIndex) => {
                    let showDifferentAmountWarning = false;

                    const amount = sortedOcrLineItems?.[lineIndex]?.amount;

                    const recognizedAmount = typeof amount === 'number' ? amount : undefined;

                    if (recognizedAmount && li.amount) {
                        showDifferentAmountWarning = li.amount !== recognizedAmount;
                    }

                    const warningLevel = showDifferentAmountWarning ? WarningLevel.Yellow : WarningLevel.None;

                    return (
                        <WarningIndicator
                            show={showDifferentAmountWarning}
                            content={
                                <TwoDifferentValuesWarning
                                    value1={li.amount}
                                    value2={recognizedAmount}
                                    label1={messages.differentAmountWarningLabel1}
                                    label2={messages.differentAmountWarningLabel2}
                                    description={messages.differentAmountWarningDescription}
                                />
                            }
                        >
                            {readonly ? (
                                <>
                                    <ReadOnlyCell
                                        text={li.amount != null ? intl.formatNumber(li.amount, 'auto') : ''}
                                    />

                                    <CellIndicator $warningLevel={warningLevel} />
                                </>
                            ) : (
                                <FocusImplementer>
                                    {({ registerFocusable }) => (
                                        <>
                                            <NumberEditor
                                                qa={qa('cell-editor')}
                                                ref={registerFocusable}
                                                allowFloat
                                                precision={constants.qBooksConstants.PRECISION_SMALL}
                                                invalid={invalid}
                                                value={li.amount ? li.amount : null}
                                                max={constants.qBooksConstants.MAX_AMOUNT}
                                                min={constants.qBooksConstants.MIN_AMOUNT}
                                                onChange={(newAmount) => {
                                                    // additionally, skip for a new lines
                                                    if (li.amount && (!newAmount || li.amount < (newAmount ?? 0))) {
                                                        onChange(
                                                            () =>
                                                                dispatch(
                                                                    changeQbooksAccountLineItemAmount(li.id, newAmount)
                                                                ),
                                                            {
                                                                isAccountLine: true,
                                                                lineId: li.id,
                                                            }
                                                        ).catch(() => {
                                                            const prevAmount =
                                                                typeof li.amount === 'number' ? li.amount : null;

                                                            // this is a dirty-hack because we need a way to revert a prev
                                                            // state, but we can't just pass a `value` prop, because
                                                            // `NumberEditor` controllable implementation is bad
                                                            dispatch(changeQbooksAccountLineItemAmount(li.id, -1));
                                                            queueMicrotask(() => {
                                                                dispatch(
                                                                    changeQbooksAccountLineItemAmount(li.id, prevAmount)
                                                                );
                                                            });
                                                        });
                                                    } else {
                                                        dispatch(changeQbooksAccountLineItemAmount(li.id, newAmount));
                                                    }
                                                }}
                                                placeholder={messages.cellPlaceholderAmount}
                                                theme='transparent'
                                                align='right'
                                                displayFormat='currency-auto'
                                                allowCalculation
                                            />

                                            <CellIndicator $warningLevel={warningLevel} />
                                        </>
                                    )}
                                </FocusImplementer>
                            )}
                        </WarningIndicator>
                    );
                },
            },
            ...(isManualMatchingShown && isBill
                ? [
                      {
                          id: 'linked',
                          name: messages.colNameLinked,
                          cell: (li) => {
                              if (isQBOMatchingOnEditing) {
                                  if (noMatchingPossible || !li.account?.id || !li.amount) {
                                      return <span />;
                                  }

                                  // `onRemoveMatching` will be defined for isBill. this condition is only for
                                  // TS complain satisfaction
                                  if (li.matchedLineItemId && onRemoveMatching) {
                                      return (
                                          <ComponentErrorBoundary>
                                              <QBooksMatchingInfo
                                                  lineItemId={li.id}
                                                  profileId={profile.id}
                                                  request={
                                                      request as Extract<
                                                          typeof request,
                                                          | selectors.types.QBooksBillExpandedRequest
                                                          | selectors.types.QBooksPoExpandedRequest
                                                      >
                                                  }
                                                  onRemoveMatching={handleRemoveMatching}
                                                  matchingInfoNormalizer={matchingInfoNormalizer}
                                                  isNewLineItem={li.isNew}
                                              />
                                          </ComponentErrorBoundary>
                                      );
                                  }

                                  if (isMatchingAvailable) {
                                      return (
                                          <NewButton noPadding onClick={() => setAccountLineItem(li)}>
                                              <AddMatchingIcon />
                                          </NewButton>
                                      );
                                  }

                                  // remove this line after removing `isQBOMatchingOnEditing`
                                  // condition above
                                  return null;
                              }

                              return li.isMatched ? (
                                  <span className={bem('linked')}>
                                      <MatchingIcon />
                                  </span>
                              ) : (
                                  <span />
                              );
                          },
                          fixedWidth: 60,
                      } as ColumnDefinition<ExpandedQBooksAccountLineItem>,
                  ]
                : []),
        ];
    }, [
        qbooksContext.hasClassFeature,
        qbooksContext.classField,
        qbooksContext.hasCustomerFeature,
        qbooksContext.customerField,
        qbooksContext.hasBillableFeature,
        qbooksContext.hasTaxableFeature,
        qbooksContext.accounts,
        qbooksContext.isTrackingTaxesEnabled,
        qbooksContext.classes,
        requiredFields.fieldIds,
        isApproverMode,
        lineAmountType,
        isBill,
        isExpense,
        isManualMatchingShown,
        dispatch,
        isOcrLoading,
        isValidationErrorsShown,
        sortedOcrLineItems,
        qa,
        bem,
        onChange,
        taxCodesList,
        classes,
        isQBOMatchingOnEditing,
        noMatchingPossible,
        onRemoveMatching,
        isMatchingAvailable,
        profile.id,
        request,
        handleRemoveMatching,
        matchingInfoNormalizer,
        setAccountLineItem,
    ]);
};
